import { SVGProps } from 'react'

import { stegaClean } from 'next-sanity'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/app/utils/style'
import { hyphenToCamelCase } from '@/app/utils/text'
import { icons, type LocalIconName } from '@/icons'
import { type FontAwesomeStyle, getFontAwesomeIcon } from '@/lib/fontawesome-generated'
import { getMuiIconMarkup, type MuiStyle } from '@/lib/mui-generated'
import { Icon as SanityIcon } from '@/sanity.types'
import { DeepRequiredNullable } from '@/types/utils'

type IconWithQueryProjection = DeepRequiredNullable<SanityIcon>

type BaseSVGProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>

type PreIconProps =
  | {
      name: LocalIconName
      provider?: 'local'
      icon?: never
    }
  | {
      name: string
      provider: 'fontawesome'
      style?: FontAwesomeStyle
      icon?: never
    }
  | {
      name: string
      provider: 'mui'
      style?: MuiStyle
      icon?: never
    }
  | {
      name?: never
      provider?: null
      style?: never
      icon: IconWithQueryProjection
    }

export type IconProps = PreIconProps &
  BaseSVGProps & {
    size?: number
  }

function isIconObject(
  props: IconProps,
): props is IconProps & { icon: IconWithQueryProjection; name?: never } {
  return 'icon' in props && props.icon !== undefined
}

export const Icon = (props: IconProps) => {
  const { size = 24, className = '', ...rest } = props

  if (isIconObject(props)) {
    const { icon } = props

    // Clean stega encoding from entire Sanity object
    const cleanIcon = stegaClean(icon)

    const iconName = cleanIcon.name || ''
    const provider = (cleanIcon.provider as 'local' | 'fontawesome' | 'mui' | undefined) || 'local'

    if (!iconName) {
      return null
    }

    if (provider === 'fontawesome') {
      const style = (cleanIcon.style as FontAwesomeStyle | undefined) || 'solid'
      const camelCaseIconName = hyphenToCamelCase(iconName)
      const faIcon = getFontAwesomeIcon(camelCaseIconName, style)
      if (!faIcon) {
        return null
      }
      return (
        <FontAwesomeIcon
          icon={faIcon}
          style={{ height: size, width: size }}
          className={cn('object-contain', className)}
        />
      )
    }

    if (provider === 'mui') {
      const style = (cleanIcon.style as MuiStyle | undefined) || 'outlined'
      const markup = getMuiIconMarkup(iconName, style)
      if (!markup) {
        return null
      }
      // Prebaked static markup from a trusted package, never user input
      return (
        <svg
          viewBox="0 0 24 24"
          width={size}
          height={size}
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
          className={className}
          dangerouslySetInnerHTML={{ __html: markup }}
        />
      )
    }

    const IconComponent = icons[iconName as LocalIconName]
    if (!IconComponent) {
      return null
    }
    return (
      <IconComponent
        height={size}
        preserveAspectRatio="xMidYMid meet"
        className={cn('w-auto', className)}
        {...rest}
      />
    )
  }

  const provider = props.provider || 'local'
  const iconName = props.name

  const cleanIconName = stegaClean(iconName) || iconName

  if (provider === 'fontawesome') {
    const style = (props.style as FontAwesomeStyle) || 'solid'
    const camelCaseIconName = hyphenToCamelCase(cleanIconName)
    const faIcon = getFontAwesomeIcon(camelCaseIconName, style)
    if (!faIcon) {
      return null
    }
    return (
      <FontAwesomeIcon
        icon={faIcon}
        style={{ height: size, width: size }}
        className={cn('object-contain', className)}
      />
    )
  }

  if (provider === 'mui') {
    const style = (props.style as MuiStyle) || 'outlined'
    const markup = getMuiIconMarkup(cleanIconName, style)
    if (!markup) {
      return null
    }
    // Prebaked static markup from a trusted package, never user input
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
        className={className}
        dangerouslySetInnerHTML={{ __html: markup }}
      />
    )
  }

  const IconComponent = icons[cleanIconName as LocalIconName]
  if (!IconComponent) {
    return null
  }
  return (
    <IconComponent
      height={size}
      preserveAspectRatio="xMidYMid meet"
      className={cn('w-auto', className)}
      {...rest}
    />
  )
}
